import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../api/client';

const STATUSES = ['new', 'contacted', 'qualified', 'invited', 'dropped'];

const LeadsPipeline = () => {
  const [leads, setLeads] = useState([]);
  const [me, setMe] = useState(null);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [invites, setInvites] = useState({}); // lead id -> {code, expires_at}

  const load = useCallback(async () => {
    try {
      const [l, m] = await Promise.all([
        api.get('/api/leads/', { params: filter ? { status: filter } : {} }),
        api.get('/api/users/me/'),
      ]);
      setLeads(l.data);
      setMe(m.data);
    } catch (e) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const patch = async (lead, body) => {
    try {
      const res = await api.patch(`/api/leads/${lead.id}/`, body);
      setLeads((ls) => ls.map((x) => (x.id === lead.id ? res.data : x)));
    } catch (e) {
      toast.error(e.response?.data?.assigned_rm?.[0] || 'Update failed');
    }
  };

  const invite = async (lead) => {
    try {
      const res = await api.post(`/api/leads/${lead.id}/invite/`, {});
      setInvites((i) => ({ ...i, [lead.id]: res.data }));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not issue invite');
    }
  };

  const shareLink = (lead, inv) => {
    const url = `${window.location.origin}/register?code=${inv.code}`;
    const text = `Hello ${lead.name.split(' ')[0]}, here is your FraxLandShare invite: ${url} (valid until ${new Date(inv.expires_at).toLocaleDateString('en-IN')}).`;
    return { url, wa: `https://wa.me/91${lead.phone}?text=${encodeURIComponent(text)}` };
  };

  return (
    <main style={{ maxWidth: 1100, margin: '0 auto', padding: '2rem 1rem' }}>
      <h1>Lead pipeline</h1>
      <label>Status{' '}
        <select value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Filter by status">
          <option value="">All</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </label>
      {loading ? <p>Loading…</p> : leads.length === 0 ? <p>No leads.</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem' }}>
            <thead>
              <tr>{['Name', 'Phone', 'Interest', 'Status', 'RM', 'Notes', 'Invite'].map((h) => <th key={h} style={{ textAlign: 'left', padding: 6 }}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {leads.map((l) => {
                const inv = invites[l.id];
                return (
                  <tr key={l.id} style={{ borderTop: '1px solid #ddd', verticalAlign: 'top' }}>
                    <td style={{ padding: 6 }}>{l.name}<br /><small>{l.email}</small></td>
                    <td style={{ padding: 6 }}>{l.phone}</td>
                    <td style={{ padding: 6 }}>{l.interest}</td>
                    <td style={{ padding: 6 }}>
                      <select value={l.status} onChange={(e) => patch(l, { status: e.target.value })} aria-label={`Status for ${l.name}`}>
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: 6 }}>
                      {l.assigned_rm_name || (me && <button onClick={() => patch(l, { assigned_rm: me.id })}>Assign to me</button>)}
                    </td>
                    <td style={{ padding: 6 }}>
                      <textarea defaultValue={l.notes} rows={2} aria-label={`Notes for ${l.name}`}
                        onBlur={(e) => e.target.value !== l.notes && patch(l, { notes: e.target.value })} />
                    </td>
                    <td style={{ padding: 6 }}>
                      {inv ? (
                        <div>
                          <strong>{inv.code}</strong><br />
                          <a href={shareLink(l, inv).wa} target="_blank" rel="noreferrer">Send on WhatsApp</a>
                        </div>
                      ) : (
                        <button onClick={() => invite(l)} disabled={l.status === 'dropped'}>
                          {l.open_invite ? 'Issue new invite' : 'Issue invite'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
};

export default LeadsPipeline;
